import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';

const PERSONA_CACHE_KEY = 'BZ_SELECTED_PERSONA';
const FEED_CACHE_KEY = 'BZ_FEED_CACHE';
const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes cache TTL

const getInitialCache = () => {
  try {
    const cached = localStorage.getItem(FEED_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (e) {
    return {};
  }
};

const DETAILED_FEED_CACHE_KEY = 'BZ_DETAILED_FEED_CACHE';
const getDetailedFeedCache = () => {
  try {
    const cached = localStorage.getItem(DETAILED_FEED_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch (e) {
    return [];
  }
};

const initialCache = getInitialCache();
const initialDetailedCache = getDetailedFeedCache();

const initialState = {
  homeData: {
    serviceGroups: initialCache.serviceGroups || [],
    topWorkspaces: initialCache.topWorkspaces || [],
    topPosts: initialCache.topPosts || [],
    topServices: initialCache.topServices || []
  },
  // High-performance cache map: { [classId]: { data, timestamp } }
  classCache: {},
  selectedClassId: localStorage.getItem(PERSONA_CACHE_KEY) || 'FOR_YOU',
  isLoading: false,
  isServiceGroupsLoading: false,
  error: null,
  lastFetched: null,
  
  // Detailed feed states for premium page state persistence
  detailedPosts: initialDetailedCache,
  detailedPage: 1,
  detailedHasMore: true,

  // Paginated Top Providers & Services
  topProviders: [],
  topProvidersPage: 1,
  topProvidersHasMore: true,
  isTopProvidersLoading: false,
  
  paginatedTopServices: [],
  topServicesPage: 1,
  topServicesHasMore: true,
  isTopServicesLoading: false
};

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setServiceGroupsLoading: (state, action) => {
      state.isServiceGroupsLoading = action.payload;
    },
    setTopProvidersLoading: (state, action) => {
      state.isTopProvidersLoading = action.payload;
    },
    setTopServicesLoading: (state, action) => {
      state.isTopServicesLoading = action.payload;
    },
    // Main action to update UI and Cache simultaneously
    setHomeData: (state, action) => {
      const { classId, className, topWorkspaces, topPosts, topServices, isFromCache } = action.payload;

      // Update Active UI
      state.homeData.topWorkspaces = topWorkspaces || state.homeData.topWorkspaces;
      state.homeData.topPosts = topPosts || state.homeData.topPosts;
      state.homeData.topServices = topServices || state.homeData.topServices;
      if (className) state.selectedClassName = className;

      // Update Cache for this specific class
      if (classId) {
        if (!state.classCache[classId]) state.classCache[classId] = {};
        state.classCache[classId].topWorkspaces = topWorkspaces;
        state.classCache[classId].topPosts = topPosts;
        state.classCache[classId].topServices = topServices;
        state.classCache[classId].className = className;
        state.classCache[classId].lastFetched = Date.now();
      }

      state.lastFetched = Date.now();
      state.error = null;

      // Persist to LocalStorage for instant loading next time
      // We only persist if this is NOT already from the cache to prevent redundant writes
      if (!isFromCache) {
        try {
          const feedCache = {
            ...state.homeData,
          // Only keep top 5 posts/workspaces to keep localStorage lean
          topPosts: (state.homeData.topPosts || []).slice(0, 5),
          topWorkspaces: (state.homeData.topWorkspaces || []).slice(0, 5),
          };
          localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(feedCache));
        } catch (e) {
          console.warn("Failed to persist feed to localStorage", e);
        }
      }
    },
    setServiceGroups: (state, action) => {
      const { classId, serviceGroups, isFromCache } = action.payload;

      // Update Active UI
      state.homeData.serviceGroups = serviceGroups;

      // Update Cache
      if (classId) {
        if (!state.classCache[classId]) state.classCache[classId] = {};
        state.classCache[classId].serviceGroups = serviceGroups;
        state.classCache[classId].lastFetched = Date.now();
      }

      if (!isFromCache) {
        try {
          const feedCache = {
            ...state.homeData,
            serviceGroups: serviceGroups || [],
          };
          localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(feedCache));
        } catch (e) {
          console.warn("Failed to persist service groups to localStorage", e);
        }
      }
    },
    // Action to quickly restore data from cache into the active homeData
    restoreFromCache: (state, action) => {
      const classId = action.payload;
      const cached = state.classCache[classId];
      if (cached) {
        state.homeData = {
          serviceGroups: cached.serviceGroups || [],
          topWorkspaces: cached.topWorkspaces || [],
          topPosts: cached.topPosts || [],
          topServices: cached.topServices || []
        };
        state.selectedClassName = cached.className || 'Providers';
      }
    },
    setSelectedClassId: (state, action) => {
      state.selectedClassId = action.payload;
      if (action.payload) {
        localStorage.setItem(PERSONA_CACHE_KEY, action.payload);
      } else {
        localStorage.removeItem(PERSONA_CACHE_KEY);
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isServiceGroupsLoading = false;
    },
    clearDiscoveryCache: (state) => {
      state.homeData = {
        serviceGroups: [],
        topWorkspaces: [],
        topPosts: [],
        topServices: []
      };
      state.classCache = {};
      state.lastFetched = null;
      state.selectedClassId = null;
      localStorage.removeItem(PERSONA_CACHE_KEY);
      localStorage.removeItem(FEED_CACHE_KEY);
      
      // Also clear detailed feed
      state.detailedPosts = [];
      state.detailedPage = 1;
      state.detailedHasMore = true;
    },
    setDetailedPosts: (state, action) => {
      state.detailedPosts = action.payload;
      try {
        localStorage.setItem(DETAILED_FEED_CACHE_KEY, JSON.stringify((action.payload || []).slice(0, 10)));
      } catch (e) {
        console.warn("Failed to persist detailed feed to localStorage", e);
      }
    },
    appendDetailedPosts: (state, action) => {
      state.detailedPosts = [...state.detailedPosts, ...action.payload];
    },
    setDetailedPage: (state, action) => {
      state.detailedPage = action.payload;
    },
    setDetailedHasMore: (state, action) => {
      state.detailedHasMore = action.payload;
    },
    clearDetailedFeed: (state) => {
      state.detailedPosts = [];
      state.detailedPage = 1;
      state.detailedHasMore = true;
    },
    
    // Paginated Top Providers
    setTopProviders: (state, action) => {
      state.topProviders = action.payload;
    },
    appendTopProviders: (state, action) => {
      state.topProviders = [...state.topProviders, ...action.payload];
    },
    setTopProvidersPage: (state, action) => {
      state.topProvidersPage = action.payload;
    },
    setTopProvidersHasMore: (state, action) => {
      state.topProvidersHasMore = action.payload;
    },
    clearTopProviders: (state) => {
      state.topProviders = [];
      state.topProvidersPage = 1;
      state.topProvidersHasMore = true;
    },

    // Paginated Top Services
    setPaginatedTopServices: (state, action) => {
      state.paginatedTopServices = action.payload;
    },
    appendPaginatedTopServices: (state, action) => {
      state.paginatedTopServices = [...state.paginatedTopServices, ...action.payload];
    },
    setTopServicesPage: (state, action) => {
      state.topServicesPage = action.payload;
    },
    setTopServicesHasMore: (state, action) => {
      state.topServicesHasMore = action.payload;
    },
    clearPaginatedTopServices: (state) => {
      state.paginatedTopServices = [];
      state.topServicesPage = 1;
      state.topServicesHasMore = true;
    },

    // Optimistic UI updates for likes
    togglePostLike: (state, action) => {
      const { postId, isLiked, likesCount } = action.payload;
      
      const updatePostsArray = (posts) => {
        if (!posts) return;
        const index = posts.findIndex(p => p._id === postId || p.id === postId);
        if (index !== -1) {
          posts[index].isLiked = isLiked;
          posts[index].likesCount = likesCount;
        }
      };

      // Update in homeData
      updatePostsArray(state.homeData.topPosts);
      
      // Update in detailed feed
      updatePostsArray(state.detailedPosts);

      // Update in class cache
      Object.keys(state.classCache).forEach(classId => {
        if (state.classCache[classId].topPosts) {
          updatePostsArray(state.classCache[classId].topPosts);
        }
      });
    }
  },
  extraReducers: (builder) => {
    // Cross-slice sync: Listen to togglePostLikeInFeed from PostSlice (Zap Watch/Immersive Feed)
    builder.addCase('post/togglePostLikeInFeed', (state, action) => {
      const { postId, isLiked, likesCount } = action.payload;
      
      const updatePostsArray = (posts) => {
        if (!posts) return;
        const index = posts.findIndex(p => p._id === postId || p.id === postId);
        if (index !== -1) {
          posts[index].isLiked = isLiked;
          posts[index].likesCount = likesCount;
        }
      };

      // 1. Update Home Top Posts
      updatePostsArray(state.homeData.topPosts);
      
      // 2. Update Detailed Posts Feed
      updatePostsArray(state.detailedPosts);
      
      // 3. Update Class Cache
      Object.keys(state.classCache).forEach(classId => {
        if (state.classCache[classId].topPosts) {
          updatePostsArray(state.classCache[classId].topPosts);
        }
      });

      // Update Local Storage Cache
      localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(state.homeData));
      localStorage.setItem(DETAILED_FEED_CACHE_KEY, JSON.stringify(state.detailedPosts));
    });
  }
});

export const {
  setLoading,
  setServiceGroupsLoading,
  setHomeData,
  setServiceGroups,
  restoreFromCache,
  setSelectedClassId,
  setError,
  clearDiscoveryCache,
  setDetailedPosts,
  appendDetailedPosts,
  setDetailedPage,
  setDetailedHasMore,
  clearDetailedFeed,
  
  setTopProviders,
  appendTopProviders,
  setTopProvidersLoading,
  setTopServicesLoading,
  setTopProvidersPage,
  setTopProvidersHasMore,
  clearTopProviders,

  setPaginatedTopServices,
  appendPaginatedTopServices,
  setTopServicesPage,
  setTopServicesHasMore,
  clearPaginatedTopServices,
  togglePostLike
} = discoverySlice.actions;

// ── Smart Thunks (Cache-Aware) ───────────────────────────────────────────────

/**
 * Fetch Home Discovery Data with Intelligent Caching
 */
export const fetchHomeDiscovery = (classId = null) => async (dispatch, getState) => {
  const state = getState().discovery;
  const targetId = classId || state.selectedClassId;

  // 🛡️ Cache Guard: STALE-WHILE-REVALIDATE pattern
  // Step 1: Immediately restore from in-memory class cache for instant UI
  if (targetId && state.classCache[targetId]) {
    const cached = state.classCache[targetId];
    if (cached.topWorkspaces || cached.topPosts) {
      dispatch(restoreFromCache(targetId));
    }
    // Step 2: If cache is fresh (< 5 min), skip background API call entirely
    // This is the key optimization — no network, no loading, pure instant render
    if (cached.lastFetched && (Date.now() - cached.lastFetched) < CACHE_TIMEOUT) {
      return;
    }
  }

  try {
    // Only show loading spinner if we don't have ANY initial data
    const hasInitialData = state.homeData.topPosts?.length > 0 || state.homeData.topWorkspaces?.length > 0;
    if (!hasInitialData) {
      dispatch(setLoading(true));
    }
    
    const url = (targetId && targetId !== 'FOR_YOU') ? `discovery/home?classId=${targetId}` : 'discovery/home';
    const response = await axiosInstance.get(url);

    if (response.data.success) {
      const { topWorkspaces, topPosts, topServices, selectedClassId: autoClassId, selectedClassName } = response.data.data;
      const finalClassId = targetId || autoClassId;

      dispatch(setHomeData({
        classId: finalClassId,
        className: selectedClassName,
        topWorkspaces,
        topPosts,
        topServices
      }));

      if (!targetId && autoClassId) {
        dispatch(setSelectedClassId(autoClassId));
      }
    } else {
      dispatch(setError(response.data.message));
    }
  } catch (error) {
    dispatch(setError(error.response?.data?.message || error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch Service Groups with Intelligent Caching
 */
export const fetchServiceGroupsByClass = (classId) => async (dispatch, getState) => {
  if (!classId || classId === 'FOR_YOU') {
    dispatch(setServiceGroups({ classId, serviceGroups: [] }));
    return;
  }

  const state = getState().discovery;

  // 🛡️ Cache Guard: STALE-WHILE-REVALIDATE pattern
  if (state.classCache[classId] && state.classCache[classId].serviceGroups) {
    // Dispatch immediately to UI
    dispatch(restoreFromCache(classId));
  }

  try {
    const hasInitialData = state.homeData.serviceGroups?.length > 0;
    if (!hasInitialData) {
      dispatch(setServiceGroupsLoading(true));
    }
    
    const response = await axiosInstance.get(`discovery/service-groups-byclass/${classId}`);

    if (response.data.success) {
      dispatch(setServiceGroups({
        classId,
        serviceGroups: response.data.data
      }));
    } else {
      dispatch(setError(response.data.message));
    }
  } catch (error) {
    dispatch(setError(error.response?.data?.message || error.message));
  } finally {
    dispatch(setServiceGroupsLoading(false));
  }
};

/**
 * Fetch Paginated Top Providers
 */
export const fetchPaginatedProviders = (classId, page = 1, limit = 50) => async (dispatch) => {
  if (!classId || classId === 'FOR_YOU') {
    console.warn('[fetchPaginatedProviders] ❌ No valid classId provided (or FOR_YOU selected) — aborting.');
    return;
  }

  console.log(`[fetchPaginatedProviders] 🚀 Fetching page=${page}, classId=${classId}`);

  try {
    dispatch(setTopProvidersLoading(true));
    console.log('[fetchPaginatedProviders] ⏳ Loading: true');
    
    const url = `discovery/workspaces-byclass/${classId}?page=${page}&limit=${limit}`;
    console.log('[fetchPaginatedProviders] 🌐 API URL:', url);

    const response = await axiosInstance.get(url);
    console.log('[fetchPaginatedProviders] 📦 Raw Response:', response.data);
    
    if (response.data.success) {
      const { data, pagination } = response.data;
      console.log(`[fetchPaginatedProviders] ✅ Received ${data?.length} providers. Pagination:`, pagination);
      
      if (page === 1) {
        dispatch(setTopProviders(data));
      } else {
        dispatch(appendTopProviders(data));
      }
      
      dispatch(setTopProvidersPage(page));
      dispatch(setTopProvidersHasMore(pagination.hasMore));
    } else {
      console.error('[fetchPaginatedProviders] ❌ API returned failure:', response.data.message);
      dispatch(setError(response.data.message));
    }
  } catch (error) {
    console.error('[fetchPaginatedProviders] 💥 Network/Server Error:', error.response?.data || error.message);
    dispatch(setError(error.response?.data?.message || 'Failed to fetch providers'));
  } finally {
    dispatch(setTopProvidersLoading(false));
    console.log('[fetchPaginatedProviders] ✔️ Loading: false');
  }
};

/**
 * Fetch Paginated Top Services
 */
export const fetchPaginatedServices = (classId, page = 1, limit = 50) => async (dispatch) => {
  if (!classId || classId === 'FOR_YOU') return;

  try {
    if (page === 1) dispatch(setLoading(true));
    
    const response = await axiosInstance.get(`discovery/services-byclass/${classId}?page=${page}&limit=${limit}`);
    
    if (response.data.success) {
      const { data, pagination } = response.data;
      
      if (page === 1) {
        dispatch(setPaginatedTopServices(data));
      } else {
        dispatch(appendPaginatedTopServices(data));
      }
      
      dispatch(setTopServicesPage(page));
      dispatch(setTopServicesHasMore(pagination.hasMore));
    } else {
      dispatch(setError(response.data.message));
    }
  } catch (error) {
    dispatch(setError(error.response?.data?.message || 'Failed to fetch services'));
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Toggle Post Like
 */
export const togglePostLikeAsync = (postId, currentIsLiked, currentLikesCount) => async (dispatch) => {
  const newIsLiked = !currentIsLiked;
  const newLikesCount = newIsLiked ? (currentLikesCount + 1) : Math.max(0, currentLikesCount - 1);

  // Optimistic UI update
  dispatch(togglePostLike({ postId, isLiked: newIsLiked, likesCount: newLikesCount }));

  try {
    const response = await axiosInstance.post(`workspaces/showcase/${postId}/react`, {
      action: newIsLiked ? 'add' : 'remove'
    });

    if (!response.data.success) {
      // Revert if failed
      dispatch(togglePostLike({ postId, isLiked: currentIsLiked, likesCount: currentLikesCount }));
    }
  } catch (error) {
    // Revert if error
    dispatch(togglePostLike({ postId, isLiked: currentIsLiked, likesCount: currentLikesCount }));
  }
};

export default discoverySlice.reducer;
